/**
 * Pure-logic helpers for the Disco import layer on the playlists page.
 *
 * These functions are dependency-free (no React, no API calls) so they can be
 * unit-tested with Vitest and reused across PlaylistsPage, PlaylistCard, etc.
 *
 * Spec: docs/superpowers/specs/2026-06-30-disco-playlist-import-design.md §6
 * Plan: docs/superpowers/plans/2026-06-30-disco-playlist-import-implementation.md P3-T4
 */

import type { Playlist } from "@/lib/playlists";
import type { ReferencePlaylistListItem } from "@/lib/reference-imports";

// ── Tab filter types ──────────────────────────────────────────────────────────

export type PlaylistTab = "all" | "my-playlists" | "disco-imports";

// ── Badge variants ────────────────────────────────────────────────────────────

export interface ImportStatusBadgeProps {
  label: string;
  variant: "success" | "info" | "warning" | "error";
}

// ── Predicates ────────────────────────────────────────────────────────────────

/** True if the playlist was imported from Disco. */
export function isDiscoImport(playlist: Playlist): boolean {
  return playlist.source === "disco_import";
}

// ── Tab filter ────────────────────────────────────────────────────────────────

/**
 * Filter the full playlist list by the active tab chip.
 *
 * "all"           — return everything
 * "my-playlists"  — exclude disco_import source (show only user-curated playlists)
 * "disco-imports" — show only disco_import source
 */
export function filterPlaylistsByTab(
  playlists: Playlist[],
  tab: PlaylistTab
): Playlist[] {
  if (tab === "all") return playlists;
  if (tab === "my-playlists") return playlists.filter((p) => !isDiscoImport(p));
  if (tab === "disco-imports") return playlists.filter(isDiscoImport);
  return playlists;
}

// ── Org-visibility merge ──────────────────────────────────────────────────────

/**
 * Merge the user's own playlists (from `getUserPlaylists`, which is per-user_id)
 * with the org-scoped reference playlist list (from `GET /api/reference-imports/playlists`).
 *
 * `getUserPlaylists` only returns playlists owned by the authenticated user.
 * Org-mates' disco imports are invisible unless we also include the org-scoped list.
 *
 * This function:
 *  1. Starts with all existing playlists (the user's own).
 *  2. Converts each ReferencePlaylistListItem into a Playlist stub.
 *  3. Appends any reference playlists whose IDs are NOT already in the set.
 *  4. Returns deduplicated list.
 *
 * NOTE: The stub's tracks array is empty — PlaylistDetailPage fetches tracks lazily.
 */
export function mergeReferencePlaylists(
  ownPlaylists: Playlist[],
  referencePlaylists: ReferencePlaylistListItem[]
): Playlist[] {
  const existingIds = new Set(ownPlaylists.map((p) => p.id));

  const stubs: Playlist[] = referencePlaylists
    .filter((ref) => !existingIds.has(ref.id))
    .map((ref) => referenceListItemToPlaylist(ref));

  return [...ownPlaylists, ...stubs];
}

/**
 * Convert a `ReferencePlaylistListItem` (from the org-scoped API list)
 * into the `Playlist` shape used by the store.
 *
 * Tracks are empty — the list endpoint does not return them (lazy-loaded on detail).
 */
export function referenceListItemToPlaylist(
  ref: ReferencePlaylistListItem
): Playlist {
  return {
    id: ref.id,
    title: ref.name,
    description: undefined,
    coverUrl: ref.cover_image_url,
    tracks: [],
    createdAt: ref.created_at,
    updatedAt: ref.updated_at,
    isPublic: false, // reference playlists are org-private by default
    createdBy: ref.user_id,
    source: ref.source,
    import_status: ref.import_status,
  };
}

// ── Badge helpers ─────────────────────────────────────────────────────────────

/**
 * Returns badge props for the import_status field, or null if no badge should render.
 *
 * Status values mirror the backend `import_status` column:
 *   "downloading" | "done" | "partial" | "error" | null
 * Note: "analyzing" is NOT a valid playlist import_status (removed — minor).
 */
export function importStatusBadgeProps(
  status: string | null | undefined
): ImportStatusBadgeProps | null {
  if (!status) return null;

  switch (status) {
    case "done":
      return { label: "Done", variant: "success" };
    case "downloading":
      return { label: "Downloading", variant: "info" };
    case "partial":
      return { label: "Partial", variant: "warning" };
    case "error":
      return { label: "Error", variant: "error" };
    default:
      return { label: status, variant: "info" };
  }
}
